import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CTASection } from "@/components/CTASection";
import { getRenderedLocation } from "@/lib/content.functions";
import { extractHeadings } from "@/lib/internal-links";

export const Route = createFileRoute("/servicos/$slug")({
  loader: async ({ params }) => {
    const loc = await getRenderedLocation({ data: { slug: params.slug } });
    if (!loc) throw notFound();
    return loc;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Serviço local | SVG Multimídia" }] };
    const url = `https://www.svgmultimidia.com.br/servicos/${params.slug}`;
    return {
      meta: [
        { title: `${loaderData.title} | SVG Multimídia` },
        { name: "description", content: loaderData.description },
        { property: "og:title", content: loaderData.title },
        { property: "og:description", content: loaderData.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: loaderData.title,
            description: loaderData.description,
            areaServed: {
              "@type": "Place",
              name: loaderData.neighborhood
                ? `${loaderData.neighborhood}, ${loaderData.city}`
                : loaderData.city,
            },
            provider: { "@type": "Organization", name: "SVG Multimídia" },
          }),
        },
      ],
    };
  },
  component: LocationPage,
  notFoundComponent: () => (
    <Section><p>Página não encontrada.</p></Section>
  ),
});

function LocationPage() {
  const loc = Route.useLoaderData() as Awaited<ReturnType<typeof getRenderedLocation>> & { html: string };
  const headings = extractHeadings(loc.html);
  const where = loc.neighborhood ? `${loc.neighborhood} — ${loc.city}` : loc.city;
  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-10 pb-10 md:pt-16 md:pb-12">
          <Breadcrumbs items={[
            { label: "Início", to: "/" },
            { label: loc.service_label },
            { label: where },
          ]} />
          <div className="mt-6 max-w-3xl">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">{loc.service_label} · {where}</span>
            <h1 className="mt-3 text-3xl font-bold tracking-tight md:text-5xl">{loc.title}</h1>
            <p className="mt-4 text-muted-foreground md:text-lg">{loc.description}</p>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[1fr_240px]">
          <article className="prose-svg">
            <div
              className="post-html space-y-5 text-base leading-relaxed text-muted-foreground [&_h2]:mt-10 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-foreground md:[&_h2]:text-3xl [&_h3]:mt-8 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-foreground [&_p]:mt-4 [&_ul]:mt-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mt-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_strong]:text-foreground [&_a]:text-primary [&_a:hover]:underline"
              dangerouslySetInnerHTML={{ __html: loc.html }}
            />
            <div className="my-10 rounded-2xl border border-primary/30 bg-primary/5 p-6">
              <p className="font-semibold">Quer levar {loc.service_label.toLowerCase()} para sua empresa em {where}?</p>
              <p className="mt-1 text-sm text-muted-foreground">Solicite um diagnóstico gratuito com a SVG Multimídia.</p>
              <Link to="/contato" className="btn-primary mt-4">Falar com a SVG</Link>
            </div>
          </article>

          {headings.length > 0 && (
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="card-glass">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nesta página</p>
                <nav className="mt-3 space-y-1 text-sm" aria-label="Sumário">
                  {headings.map((h) => (
                    <a
                      key={h.id}
                      href={`#${h.id}`}
                      className={`block hover:text-primary ${h.level === 3 ? "pl-3 text-xs text-muted-foreground" : "text-foreground"}`}
                    >
                      {h.text}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>
          )}
        </div>
      </Section>

      <CTASection />
    </>
  );
}